import type { EditTarget } from '@web-studio/shared-schemas';
import type { ComponentPropsWithoutRef, MouseEvent } from 'react';

type EditableMediaProps = {
  fieldKey: string;
  activeEditTarget?: EditTarget | null;
  editable?: boolean;
} & ComponentPropsWithoutRef<'img'>;

/**
 * Media edit target — always a single `<img>` so edit/view layout parity holds.
 * (A wrapper span used to break absolute-fill frames: aspect-ratio + % height quirks
 * showed the media surface as a brown band under the photo in Studio edit mode.)
 * Selection chrome uses inset box-shadow on the img (replaced elements have no ::after).
 */
export function EditableMedia({
  fieldKey,
  activeEditTarget,
  editable,
  className,
  src,
  onClick,
  ...rest
}: EditableMediaProps) {
  if (typeof src !== 'string' || !src.trim()) {
    return null;
  }

  const isActive =
    Boolean(editable) &&
    activeEditTarget?.kind === 'media-field' &&
    activeEditTarget.fieldKey === fieldKey;

  const handleClick = (event: MouseEvent<HTMLImageElement>) => {
    if (editable) {
      event.preventDefault();
    }
    onClick?.(event);
  };

  if (!editable) {
    return (
      <img
        src={src}
        className={className}
        onClick={onClick}
        onError={(event) => {
          // Drop broken src so CSS `:has([src])` no longer hides the wordmark.
          event.currentTarget.removeAttribute('src');
        }}
        {...rest}
      />
    );
  }

  const editAttrs = {
    'data-edit-child': 'media-field',
    'data-field-key': fieldKey,
    ...(isActive ? { 'data-media-active': 'true' as const } : {}),
  } as const;

  const imgClass = [
    'ws-edit-media',
    // Keep legacy hook so existing `.foo .ws-edit-media__img` fill rules still match.
    'ws-edit-media__img',
    className,
    isActive ? 'ws-edit-child--active ws-edit-media--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <img
      src={src}
      className={imgClass}
      {...editAttrs}
      onClick={handleClick}
      onError={(event) => {
        event.currentTarget.removeAttribute('src');
      }}
      {...rest}
    />
  );
}
